import React, { useState, useEffect, useRef } from 'react';
import { Star, ChevronDown } from 'lucide-react';

interface Review {
  id: string;
  author: string;
  rating: number;
  reviewBody: string;
  datePublished: string;
}

interface ReviewSectionProps {
  reviews: Review[];
  compact?: boolean;
  showStructuredData?: boolean;
}

const TRUNCATE_LENGTH = 110;
const VISIBLE_COUNT = 3;
const ROTATE_MS = 5000;

export const ReviewSection: React.FC<ReviewSectionProps> = ({ 
  reviews, 
  compact = false,
  showStructuredData = true 
}) => {
  const [expandedReviews, setExpandedReviews] = useState<Set<string>>(new Set());
  const [startIndex, setStartIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const averageRating = reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length;
  const reviewCount = reviews.length;

  const shouldRotate = reviewCount > VISIBLE_COUNT;

  useEffect(() => {
    if (!shouldRotate || isPaused) return;
    const id = window.setInterval(() => {
      setStartIndex((prev) => (prev + 1) % reviewCount);
    }, ROTATE_MS);
    return () => window.clearInterval(id);
  }, [shouldRotate, isPaused, reviewCount]);

  const visibleReviews = shouldRotate
    ? Array.from({ length: VISIBLE_COUNT }, (_, i) => reviews[(startIndex + i) % reviewCount])
    : reviews;

  const pageCount = reviewCount;

  const toggleReview = (reviewId: string) => {
    const newExpanded = new Set(expandedReviews);
    if (newExpanded.has(reviewId)) {
      newExpanded.delete(reviewId);
    } else {
      newExpanded.add(reviewId);
    }
    setExpandedReviews(newExpanded);
  };

  const truncateText = (text: string, maxLength: number): string => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  };

  return (
    <>
      {/* JSON-LD Structured Data - LocalBusiness (voor Google sterren en reviews) */}
      {showStructuredData && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "LocalBusiness",
              "name": "Systemique - Familieopstellingen & Coaching",
              "image": "https://systemique.nl/logo.png",
              "telephone": "+31-6-12991453",
              "priceRange": "€€",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Castricum",
                "addressRegion": "Noord-Holland",
                "addressCountry": "NL"
              },
              "url": "https://systemique.nl/familieopstellingen",
              "aggregateRating": {
                "@type": "AggregateRating",
                "ratingValue": averageRating.toFixed(1),
                "reviewCount": reviewCount.toString(),
                "bestRating": "5",
                "worstRating": "1"
              },
              "review": reviews.map(review => ({
                "@type": "Review",
                "author": {
                  "@type": "Person",
                  "name": review.author
                },
                "reviewRating": {
                  "@type": "Rating",
                  "ratingValue": review.rating.toString(),
                  "bestRating": "5",
                  "worstRating": "1"
                },
                "reviewBody": review.reviewBody,
                "datePublished": review.datePublished
              }))
            })
          }}
        />
      )}

      {/* Visuele Review Sectie */}
      <section className={`${compact ? 'py-12 md:py-16' : 'py-16 md:py-24'} px-6 bg-[#FAEBEE] relative`}>
        <div className="container mx-auto max-w-6xl">
          {/* Header */}
          <div className={`text-center ${compact ? 'mb-8' : 'mb-12'}`}>
            <span className={`text-[#854C73] font-bold tracking-widest uppercase ${compact ? 'text-xs' : 'text-xs'} mb-4 block`}>
              Ervaringen
            </span>
            <h2 className={`font-serif ${compact ? 'text-3xl md:text-4xl' : 'text-4xl md:text-5xl'} text-[#854C73] mb-4`}>
              Wat anderen zeggen
            </h2>
            <div className="w-24 h-1 bg-[#854C73]/20 mx-auto rounded-full mt-6"></div>
          </div>

          {/* Reviews Grid — auto-rotating carousel wanneer > 3 reviews */}
          <div
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            onFocusCapture={() => setIsPaused(true)}
            onBlurCapture={() => setIsPaused(false)}
          >
          <div
            key={shouldRotate ? `page-${startIndex}` : 'static'}
            className={`grid grid-cols-1 md:grid-cols-3 ${compact ? 'gap-6' : 'gap-8'} ${shouldRotate ? 'animate-fade-up' : ''}`}
          >
            {visibleReviews.map((review) => {
              const isExpanded = expandedReviews.has(review.id);
              const needsTruncation = review.reviewBody.length > TRUNCATE_LENGTH;
              const displayText = needsTruncation && !isExpanded
                ? truncateText(review.reviewBody, TRUNCATE_LENGTH)
                : review.reviewBody;

              return (
                <div
                  key={review.id}
                  className={`bg-white rounded-2xl shadow-lg border border-[#FAEBEE] hover:shadow-xl transition-shadow ${compact ? 'p-5 md:p-6' : 'p-6 md:p-8'}`}
                >
                  {/* Sterren */}
                  <div className={`flex gap-1 ${compact ? 'mb-3' : 'mb-4'}`}>
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`${compact ? 'w-4 h-4' : 'w-5 h-5'} ${
                          i < review.rating
                            ? 'fill-[#854C73] text-[#854C73]'
                            : 'fill-gray-200 text-gray-200'
                        }`}
                      />
                    ))}
                  </div>

                  {/* Review Tekst */}
                  <p className={`text-[#6b4912] leading-relaxed ${compact ? 'mb-3 text-sm' : 'mb-4'} italic`}>
                    "{displayText}"
                  </p>

                  {/* Lees meer/Lees minder knop */}
                  {needsTruncation && (
                    <button
                      onClick={() => toggleReview(review.id)}
                      className={`text-[#854C73] hover:text-[#590D0D] font-medium flex items-center gap-1 transition-colors ${compact ? 'text-xs mb-4' : 'text-sm mb-6'}`}
                    >
                      {isExpanded ? (
                        <>
                          Lees minder
                          <ChevronDown className="w-4 h-4 rotate-180 transition-transform" />
                        </>
                      ) : (
                        <>
                          Lees meer
                          <ChevronDown className="w-4 h-4 transition-transform" />
                        </>
                      )}
                    </button>
                  )}

                  {/* Auteur */}
                  <div className={`flex items-center gap-3 ${compact ? 'pt-3' : 'pt-4'} border-t border-[#FAEBEE]`}>
                    <div className={`${compact ? 'w-8 h-8' : 'w-10 h-10'} bg-[#854C73]/10 rounded-full flex items-center justify-center`}>
                      <span className={`text-[#854C73] font-semibold ${compact ? 'text-xs' : 'text-sm'}`}>
                        {review.author.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className={`text-[#854C73] font-semibold ${compact ? 'text-xs' : 'text-sm'}`}>
                        {review.author}
                      </p>
                      <p className={`text-gray-500 ${compact ? 'text-[10px]' : 'text-xs'}`}>
                        {new Date(review.datePublished).toLocaleDateString('nl-NL', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {shouldRotate && (
            <div className="flex justify-center gap-2 mt-8" role="tablist" aria-label="Reviews navigatie">
              {Array.from({ length: pageCount }).map((_, i) => {
                const isActive = i === startIndex;
                return (
                  <button
                    key={i}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    aria-label={`Toon reviews vanaf positie ${i + 1}`}
                    onClick={() => setStartIndex(i)}
                    className={`h-2 rounded-full transition-all duration-300 ${
                      isActive ? 'w-6 bg-[#854C73]' : 'w-2 bg-[#854C73]/30 hover:bg-[#854C73]/50'
                    }`}
                  />
                );
              })}
            </div>
          )}
          </div>
        </div>
      </section>
    </>
  );
};

